import { omit } from "lodash-es";
import { BookOpen, Info } from "lucide-react";
import React, { ReactElement, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import {
  Box,
  FilterOptionsState,
  ListItem,
  ListItemText,
  createFilterOptions,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

import CircuitBreakerThresholdsSetting from "../../../../components/CircuitBreakerThresholdsSetting";
import MultiValueTextFieldGroup from "../../../../components/MultiValueTextFieldGroup";
import BasicSelector from "../../../../components/Redux/BasicSelector";
import ImageUrlTextField from "../../../../components/Redux/ImageUrlTextField";
import OptionalStringList from "../../../../components/Redux/OptionalStringList";
import { ReduxHiddenDataField } from "../../../../components/Redux/ReduxHiddenDataField";
import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import Autocomplete from "../../../../components/styled/Autocomplete";
import TabSection from "../../../../components/styled/TabSection";
import useFeatureFlag from "../../../../hooks/use-feature-flag";
import {
  getRouteFormProperty,
  setHealthCheckType,
  setIntegerRouteFormProperty,
  setRouteFormError,
  setRouteFormProperty,
  setRouteMode,
} from "../../../../slices/routes";
import { useDispatch, useSelector } from "../../../../store";
import { useListRoutesQuery } from "../../../../store/enhancedApi";
import {
  CircuitBreakerThresholds,
  Route,
  RouteTcpHealthCheck,
  useGetRouteQuery,
  useGetSettingsQuery,
} from "../../../../store/pomeriumApi";
import { validateDuration } from "../../../../validators/duration";
import { validateInteger } from "../../../../validators/integer";
import {
  validToUrl,
  validUrl,
  validateK8sServiceAccountToken,
} from "../../../../validators/url";
import { StyledTextField } from "../../../certificates/components/AddFormDialog";
import { CodecClientOptions } from "../fields/CodecClentOptions";
import { ExpectedStatusList } from "../fields/ExpectedStatusList";
import { HealthCheckOptions } from "../fields/HealthCheckOptions";
import { JwtIssuerFormatOptions } from "../fields/JwtIssuerFormatOptions";
import { LoadBalancerOptions } from "../fields/LoadBalancerOptions";
import { RedirectBuilder } from "../fields/RedirectBuilder";
import { ResponseBuilder } from "../fields/ResponseBuilder";
import { SectionProps } from "./common";
import { ReduxSliderField } from "@/components/Redux/Slider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export const Advanced = ({
  readonly,
  tabValue,
}: SectionProps): ReactElement => {
  if (tabValue !== "advanced") {
    return <></>;
  }

  return (
    <TabSection index={"advanced"} value={tabValue} title="Advanced">
      <AdvancedSettings readonly={readonly} />
    </TabSection>
  );
};

const AdvancedSettings = ({
  readonly,
}: {
  readonly: boolean;
}): ReactElement => {
  const dispatch = useDispatch();
  const routeMode = useSelector((state) => state.routes.routeMode);
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { data: settings } = useGetSettingsQuery(
    {
      organizationId: organizationId || "",
      clusterId: clusterId || "",
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const healthCheckReceive = useSelector(
    (state) => (state.routes.form?.healthCheck as RouteTcpHealthCheck)?.receive,
  );
  const healthCheckType = useSelector(
    (state) => state.routes.form?.healthCheck?.type,
  );
  const circuitBreakerThresholds = useSelector(
    (state) => state.routes.form?.circuitBreakerThresholds,
  );
  const to = useSelector((state) => state.routes.form?.to);
  const idpTokensEnabled = useFeatureFlag("idp-tokens", false);
  const dependsOnEnabled = useFeatureFlag("depends-on", false);

  const handleChangeCircuitBreakerThresholds = (
    value?: CircuitBreakerThresholds,
  ) => {
    dispatch(
      setRouteFormProperty({
        path: "circuitBreakerThresholds",
        value: value,
      }),
    );
  };

  return (
    <>
      <RadioGroup
        aria-label="RouteMode"
        name="RouteMode"
        sx={{ pt: 2 }}
        row
        value={routeMode}
        onChange={(evt): void => {
          dispatch(setRouteMode(evt.target.value as typeof routeMode));
        }}
      >
        <FormControlLabel
          value="to"
          control={<Radio color="primary" />}
          label="To"
        />
        <FormControlLabel
          value="redirect"
          control={<Radio color="primary" />}
          label="Redirect"
        />
        <FormControlLabel
          value="response"
          control={<Radio color="primary" />}
          label="Direct Response"
        />
      </RadioGroup>

      {routeMode === "to" && (
        <SettingSection label="To" docsKey="to">
          <MultiValueTextFieldGroup
            disabled={readonly}
            values={to || []}
            onSave={(values) =>
              dispatch(
                setRouteFormProperty({
                  path: "to",
                  value: values.length === 0 ? undefined : values,
                }),
              )
            }
            validator={validToUrl}
          />
        </SettingSection>
      )}

      {routeMode === "redirect" && (
        <SettingSection
          label="Redirect"
          docsKey="redirect"
          fullWidthChildren={<RedirectBuilder readonly={readonly} />}
        />
      )}

      {routeMode === "response" && (
        <SettingSection
          label="Direct Response"
          docsKey="direct-response"
          fullWidthChildren={<ResponseBuilder readonly={readonly} />}
        />
      )}
      <Divider />

      <SettingSection label="Logo URL" docsKey="route-logo-url">
        <ImageUrlTextField
          disabled={readonly}
          fullWidth
          path="logoUrl"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          validator={validUrl}
        />
      </SettingSection>

      <Divider />

      <SettingSection
        label="Load Balancing Policy"
        docsKey="load-balancing-policy"
      >
        <BasicSelector
          path="loadBalancingPolicy"
          options={LoadBalancerOptions}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          readonly={readonly}
        />
      </SettingSection>
      <Divider />

      <SettingSection
        label="Identity Provider Client ID"
        docsKey="identity-provider-client-id"
      >
        <ReduxTextField
          path="identityProviderClientId"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          spellCheck={false}
          disabled={readonly}
          fullWidth
        />
      </SettingSection>
      <SettingSection
        label="Identity Provider Client Secret"
        docsKey="identity-provider-client-secret"
      >
        <ReduxHiddenDataField
          path="identityProviderClientSecret"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          spellCheck={false}
          disabled={readonly}
          fullWidth
        />
      </SettingSection>
      <Divider />

      <SettingSection
        label="Kubernetes Service Account Token"
        docsKey="kubernetes-service-account-token"
      >
        <ReduxHiddenDataField
          path="kubernetesServiceAccountToken"
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          validator={validateK8sServiceAccountToken}
          spellCheck={false}
        />
      </SettingSection>
      <Divider />

      <SettingSection label="JWT Issuer Format" docsKey="jwt-issuer-format">
        <BasicSelector
          path="jwtIssuerFormat"
          options={JwtIssuerFormatOptions}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          readonly={readonly}
        />
      </SettingSection>
      <Divider />

      {dependsOnEnabled && (
        <>
          <SettingSection
            label="Additional Login Redirect Hosts"
            docsKey="additional-login-redirect-hosts"
          >
            <div className="flex flex-col w-full gap-2">
              <DependsOnAutocomplete
                readonly={!settings?.identityProvider || readonly}
              />
              {!settings?.identityProvider && (
                <div>
                  <Alert className="w-full bg-transparent">
                    <Info />
                    <AlertTitle>
                      Configure a custom Identity Provider to use this feature.
                    </AlertTitle>
                    <AlertDescription className="flex gap-4 mt-2">
                      <Link to="/app/settings">
                        <Button>Configure IdP</Button>
                      </Link>
                      <a
                        href="https://pomerium.com/docs/reference/routes/additional-login-redirect-hosts"
                        target="_blank"
                      >
                        <Button variant="secondary">
                          <BookOpen color="#42a5f5" className="size-5" />
                          Documentation
                        </Button>
                      </a>
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </SettingSection>
          <Divider />
        </>
      )}

      {idpTokensEnabled && (
        <>
          <SettingSection
            label="IDP Access Token Allowed Audiences"
            docsKey="idp-access-token-allowed-audiences"
          >
            <OptionalStringList
              path="idpAccessTokenAllowedAudiences"
              disabled={readonly}
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
            />
          </SettingSection>
          <Divider />
        </>
      )}

      <SettingSection label="Health Checks" docsKey="health-checks">
        <BasicSelector
          path="healthCheck.type"
          options={HealthCheckOptions}
          setter={setHealthCheckType}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
          readonly={readonly}
        />
      </SettingSection>

      {healthCheckType ? (
        <Box sx={{ pl: 4 }}>
          <Divider />

          <SettingSection label="Timeout" docsKey="health-checks-timeout">
            <ReduxTextField
              disabled={readonly}
              fullWidth
              path="healthCheck.timeout"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              validator={validateDuration}
              required={!!healthCheckType}
            />
          </SettingSection>

          <Divider />

          <SettingSection label="Interval" docsKey="health-checks-interval">
            <ReduxTextField
              disabled={readonly}
              fullWidth
              path="healthCheck.interval"
              setter={setRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              validator={validateDuration}
              required={!!healthCheckType}
            />
          </SettingSection>

          <Divider />

          <SettingSection
            label="Unhealthy Threshold"
            docsKey="health-checks-unhealthy-threshold"
          >
            <ReduxTextField
              disabled={readonly}
              setter={setIntegerRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="healthCheck.unhealthyThreshold"
              validator={validateInteger}
              required={!!healthCheckType}
            />
          </SettingSection>

          <Divider />

          <SettingSection
            label="Healthy Threshold"
            docsKey="health-checks-healthy-threshold"
          >
            <ReduxTextField
              disabled={readonly}
              setter={setIntegerRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="healthCheck.healthyThreshold"
              validator={validateInteger}
              required={!!healthCheckType}
            />
          </SettingSection>

          <Divider />

          <SettingSection
            label="Healthy Panic Threshold"
            docsKey="healthy-panic-threshold"
          >
            <ReduxSliderField
              setter={setIntegerRouteFormProperty}
              getter={getRouteFormProperty}
              errorHandler={setRouteFormError}
              path="healthyPanicThreshold"
              min={0}
              max={100}
              valueLabelDisplay="on"
            />
          </SettingSection>

          {healthCheckType === "http" && (
            <>
              <Divider />

              <SettingSection label="Host" docsKey="http-health-check-host">
                <ReduxTextField
                  disabled={readonly}
                  fullWidth
                  path="healthCheck.host"
                  setter={setRouteFormProperty}
                  getter={getRouteFormProperty}
                  errorHandler={setRouteFormError}
                />
              </SettingSection>

              <Divider />

              <SettingSection label="Path" docsKey="http-health-check-path">
                <ReduxTextField
                  disabled={readonly}
                  fullWidth
                  path="healthCheck.path"
                  setter={setRouteFormProperty}
                  getter={getRouteFormProperty}
                  errorHandler={setRouteFormError}
                />
              </SettingSection>

              <Divider />

              <SettingSection
                label="Expected Statuses"
                docsKey="http-health-check-expected-statuses"
              >
                <ExpectedStatusList readonly={readonly} />
              </SettingSection>
              <Divider />

              <SettingSection
                label="Codec Client Type"
                docsKey="http-health-check-codec-client-type"
              >
                <BasicSelector
                  path="healthCheck.codecClientType"
                  options={CodecClientOptions}
                  setter={setRouteFormProperty}
                  getter={getRouteFormProperty}
                  errorHandler={setRouteFormError}
                  readonly={readonly}
                />
              </SettingSection>
            </>
          )}

          {healthCheckType === "tcp" && (
            <>
              <Divider />

              <SettingSection label="Send" docsKey="tcp-health-check-send">
                <ReduxTextField
                  disabled={readonly}
                  fullWidth
                  path="healthCheck.send"
                  setter={setRouteFormProperty}
                  getter={getRouteFormProperty}
                  errorHandler={setRouteFormError}
                />
              </SettingSection>

              <Divider />

              <SettingSection
                label="Receive"
                docsKey="tcp-health-check-receive"
              >
                <MultiValueTextFieldGroup
                  disabled={readonly}
                  values={healthCheckReceive || []}
                  onSave={(values) => {
                    dispatch(
                      setRouteFormProperty({
                        path: "healthCheck.receive",
                        value: values.length === 0 ? undefined : values,
                      }),
                    );
                  }}
                />
              </SettingSection>
            </>
          )}

          {healthCheckType === "grpc" && (
            <>
              <Divider />

              <SettingSection
                label="Service Name"
                docsKey="grpc-health-check-service-name"
              >
                <ReduxTextField
                  disabled={readonly}
                  fullWidth
                  path="healthCheck.serviceName"
                  setter={setRouteFormProperty}
                  getter={getRouteFormProperty}
                  errorHandler={setRouteFormError}
                />
              </SettingSection>

              <Divider />

              <SettingSection
                label="Authority"
                docsKey="grpc-health-check-authority"
              >
                <ReduxTextField
                  disabled={readonly}
                  fullWidth
                  path="healthCheck.authority"
                  setter={setRouteFormProperty}
                  getter={getRouteFormProperty}
                  errorHandler={setRouteFormError}
                />
              </SettingSection>
            </>
          )}
        </Box>
      ) : (
        <></>
      )}

      <Divider />

      <CircuitBreakerThresholdsSetting
        value={circuitBreakerThresholds}
        onChange={handleChangeCircuitBreakerThresholds}
      />

      <Divider />

      <SettingSection label="Allow Upgrades" docsKey="allow-upgrades">
        <OptionalStringList
          path="allowUpgrades"
          disabled={readonly}
          setter={setRouteFormProperty}
          getter={getRouteFormProperty}
          errorHandler={setRouteFormError}
        />
      </SettingSection>
    </>
  );
};

function DependsOnAutocomplete({ readonly }: { readonly: boolean }) {
  type RouteDomainOption = {
    id: string;
    domain: string;
    routeName: string;
  };

  const dispatch = useDispatch();
  const { routeId } = useParams();
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { data: route, isLoading: isRouteLoading } = useGetRouteQuery(
    {
      organizationId,
      routeId: routeId || "",
    },
    { skip: !organizationId || !routeId },
  );
  const { data: result, isLoading: isListRoutesLoading } = useListRoutesQuery(
    {
      organizationId,
      namespaceId: clusterId,
    },
    { skip: !organizationId || !clusterId },
  );

  const fromRoutes: RouteDomainOption[] =
    result?.rows
      .filter((r: Route) => route?.from !== r.from)
      .map((r: Route) => {
        return {
          id: r.id,
          domain: r.from,
          routeName: r.name,
        };
      }) || [];

  const initialValues: RouteDomainOption[] = (route?.dependsOn || []).map(
    (domain) => {
      const existingRoute = fromRoutes.find((fr) => fr.domain === domain);
      if (existingRoute) return existingRoute;
      return {
        id: `custom_${domain}`,
        domain,
        routeName: route?.name ?? "Custom for current route",
      };
    },
  );

  const [dependsOnInput, setDependsOnInput] = useState(initialValues);

  const onChange = (_evt: React.SyntheticEvent, newDomains: unknown) => {
    setDependsOnInput(newDomains as RouteDomainOption[]);
    dispatch(
      setRouteFormProperty({
        path: "dependsOn",
        value: ((newDomains as RouteDomainOption[]) || []).map(
          (opt) => opt.domain,
        ),
      }),
    );
  };

  const filter = createFilterOptions<RouteDomainOption>();

  return (
    <Autocomplete
      className="w-full"
      multiple
      freeSolo
      autoHighlight
      loading={isRouteLoading || isListRoutesLoading}
      disabled={readonly}
      value={dependsOnInput}
      onChange={onChange}
      options={fromRoutes.map((r) => {
        return {
          ...r,
          domain: new URL(r.domain).host,
        };
      })}
      getOptionLabel={(r: unknown) => (r as RouteDomainOption).domain}
      filterOptions={(options, params) => {
        const filtered = filter(
          options as RouteDomainOption[],
          params as FilterOptionsState<RouteDomainOption>,
        );

        const { inputValue } = params;
        const exists = options.some((option) => inputValue === option);
        if (inputValue !== "" && !exists) {
          filtered.push({
            id: `custom_${inputValue}`,
            domain: inputValue,
            routeName: `Custom for ${route?.name ?? "current route"}`,
          });
        }

        return filtered;
      }}
      renderInput={(params): ReactElement => <StyledTextField {...params} />}
      renderOption={(props, r: unknown) => {
        return (
          <ListItem
            key={`${(r as RouteDomainOption)?.id}`}
            {...omit(props, "key")}
          >
            <ListItemText
              primary={(r as RouteDomainOption).domain}
              secondary={(r as RouteDomainOption).routeName}
            />
          </ListItem>
        );
      }}
    />
  );
}
